import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { SectionReveal } from '@/components/shared/SectionReveal';
import { FeaturedProjectsSlider } from '@/components/FeaturedProjectsSlider';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getProjects, getFeaturedProjects } from '@/lib/api';
import { getProjectDisplayImage, getProjectAspectRatio } from '@/lib/projectUtils';
import { Skeleton } from '@/components/ui/skeleton';
import { Play } from 'lucide-react';
import { SEO } from '@/components/SEO';

const categories = [
  { value: 'all', label: 'All' },
  { value: 'documentary', label: 'Documentary' },
  { value: 'narrative', label: 'Narrative' },
  { value: 'commercial', label: 'Commercial' },
];

export default function WorkPage() {
  const [projects, setProjects] = useState([]);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');

  // Fetch featured projects once on mount
  useEffect(() => {
    getFeaturedProjects()
      .then(setFeaturedProjects)
      .catch(err => console.error('Failed to fetch featured projects:', err));
  }, []);

  useEffect(() => {
    setLoading(true);
    getProjects(category)
      .then(setProjects)
      .finally(() => setLoading(false));
  }, [category]); // Only depend on category

  // Helper to determine if project should show in vertical format based on image preference
  const isVerticalFormat = (project) => {
    const aspectRatio = getProjectAspectRatio(project);
    return aspectRatio === 'aspect-[2/3]'; // Vertical/poster format
  };

  return (
    <div>
      <SEO 
        title="Selected Work | Baklight Films — Documentary & Commercial Portfolio"
        description="Curated film work by Baklight. Award-winning documentaries, international brand campaigns, narrative storytelling. Recognized at 27+ festivals."
        path="/work"
      />
      <PageHeader
        meta="Selected Work"
        title="Not everything — only what defines our voice."
        subtitle="Selected work across documentary, commercial and narrative storytelling."
      />

      {/* Featured Projects Slider */}
      <FeaturedProjectsSlider projects={featuredProjects} />

      <section className="pb-20 sm:pb-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          {/* Filters */}
          <div className="mb-10">
            <Tabs value={category} onValueChange={setCategory} data-testid="work-filters-tabs">
              <TabsList className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))]">
                {categories.map((cat) => (
                  <TabsTrigger
                    key={cat.value}
                    value={cat.value}
                    data-testid={`work-filter-${cat.value}-tab`}
                    className="font-mono-meta data-[state=active]:bg-[hsl(var(--bk-fg))] data-[state=active]:text-[hsl(var(--bk-bg))]"
                  >
                    {cat.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Responsive Grid - Layout adapts to filter */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Skeleton className="aspect-[2/3] rounded-lg" />
              <Skeleton className="aspect-[2/3] rounded-lg" />
              <Skeleton className="aspect-[2/3] rounded-lg" />
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-body text-[hsl(var(--bk-fg-muted))]">No projects match this cut.</p>
              <button
                onClick={() => setCategory('all')}
                className="font-body text-sm text-[hsl(var(--bk-accent))] mt-3 hover:opacity-80 transition-opacity"
                data-testid="work-reset-filters"
              >
                Reset filters
              </button>
            </div>
          ) : (
            <>
              {/* ALL FILTER: verticals + horizontals - 25% larger */}
              {category === 'all' && (
                <div className="space-y-6 lg:space-y-8">
                  {/* Row 1: All vertical format projects (6-column grid) */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                    {projects.filter(p => isVerticalFormat(p)).map((project, i) => (
                      <SectionReveal key={project.id || project.slug} delay={i * 0.05}>
                        <Link 
                          to={`/work/${project.slug}`} 
                          className="group block"
                          data-testid={`work-project-card-${project.slug}`}
                        >
                          <div className={`relative ${getProjectAspectRatio(project)} rounded-md overflow-hidden bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] mb-2`}>
                            <img
                              src={getProjectDisplayImage(project)}
                              alt={project.title}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                          </div>
                          <div className="space-y-1">
                            <p className="font-mono-meta text-[10px] text-[hsl(var(--bk-fg-subtle))] uppercase">{project.category} · {project.year}</p>
                            <h3 className="font-display text-xs text-[hsl(var(--bk-fg))] tracking-tight line-clamp-2">{project.title}</h3>
                            <p className="font-body text-[11px] text-[hsl(var(--bk-fg-muted))] leading-relaxed line-clamp-2">{project.logline}</p>
                          </div>
                        </Link>
                      </SectionReveal>
                    ))}
                  </div>

                  {/* Row 2: All horizontal format projects (2-column grid) */}
                  {projects.filter(p => !isVerticalFormat(p)).length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                      {projects.filter(p => !isVerticalFormat(p)).map((project, i) => (
                        <SectionReveal key={project.id || project.slug} delay={0.3 + i * 0.1}>
                          <Link 
                            to={`/work/${project.slug}`} 
                            className="group block"
                            data-testid={`work-project-card-${project.slug}`}
                          >
                            <div className={`relative ${getProjectAspectRatio(project)} rounded-lg overflow-hidden bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] mb-4`}>
                              <img
                                src={getProjectDisplayImage(project)}
                                alt={project.title}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                              />
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center">
                                  <Play className="w-6 h-6 text-white fill-white ml-1" />
                                </div>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <p className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] uppercase tracking-wide">{project.category} · {project.year}</p>
                              <h3 className="font-display text-base md:text-xl text-[hsl(var(--bk-fg))] tracking-tight">{project.title}</h3>
                              <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">{project.logline}</p>
                            </div>
                          </Link>
                        </SectionReveal>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* DOCUMENTARY + NARRATIVE FILTERS: Compact grid - 25% larger */}
              {(category === 'documentary' || category === 'narrative') && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {projects.map((project, i) => (
                    <SectionReveal key={project.id || project.slug} delay={i * 0.05}>
                      <Link 
                        to={`/work/${project.slug}`} 
                        className="group block"
                        data-testid={`work-project-card-${project.slug}`}
                      >
                        <div className={`relative ${getProjectAspectRatio(project)} rounded-md overflow-hidden bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] mb-2`}>
                          <img
                            src={getProjectDisplayImage(project)}
                            alt={project.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                        </div>
                        <div className="space-y-1">
                          <p className="font-mono-meta text-[10px] text-[hsl(var(--bk-fg-subtle))] uppercase">{project.category} · {project.year}</p>
                          <h3 className="font-display text-xs text-[hsl(var(--bk-fg))] tracking-tight line-clamp-2">{project.title}</h3>
                          <p className="font-body text-[11px] text-[hsl(var(--bk-fg-muted))] line-clamp-2 leading-relaxed">{project.logline}</p>
                        </div>
                      </Link>
                    </SectionReveal>
                  ))}
                </div>
              )}

              {/* COMMERCIAL FILTER: Full-width stacked horizontal */}
              {category === 'commercial' && (
                <div className="max-w-[900px] mx-auto space-y-8">
                  {projects.map((project, i) => (
                    <SectionReveal key={project.id || project.slug} delay={i * 0.1}>
                      <Link 
                        to={`/work/${project.slug}`} 
                        className="group block"
                        data-testid={`work-project-card-${project.slug}`}
                      >
                        <div className={`relative ${getProjectAspectRatio(project)} rounded-lg overflow-hidden bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] mb-4`}>
                          <img
                            src={getProjectDisplayImage(project)}
                            alt={project.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                            <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center">
                              <Play className="w-6 h-6 text-white fill-white ml-1" />
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <p className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] uppercase tracking-wide">{project.category} · {project.year}</p>
                          <h3 className="font-display text-xl md:text-2xl text-[hsl(var(--bk-fg))] tracking-tight">{project.title}</h3>
                          <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">{project.logline}</p>
                        </div>
                      </Link>
                    </SectionReveal>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
